import useGetInboundEmailUnreadCount from '@/api/queries/inbound-emails/useGetInboundEmailUnreadCount';
import useGetMe from '@/api/queries/me/useGetMe';
import { useMyAccountHasFeature } from '@/api/queries/me/useGetMyAccountFeatures';
import { useSelector } from '@/api/state';
import { usePermissions } from '@/hooks/usePermissions';
import { useShowParentAccountAdminLayout } from '@/hooks/useShowParentAccountAdminLayout';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import { Badge, MenuItemCore, Text } from '@payaca/ambrosia';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { AnalyticsPermissions } from '@payaca/permissions/analytics/analytics.permissons';
import { AutomationsPermissions } from '@payaca/permissions/automations/automations.permissions';
import { CustomersPermissions } from '@payaca/permissions/customers/customers.permissions';
import { DashboardPermissions } from '@payaca/permissions/dashboard/dashboard.permissions';
import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { InboundEmailsPermissions } from '@payaca/permissions/inbound-emails/inbound-emails.permissions';
import { InvoicesPermissions } from '@payaca/permissions/invoices/invoices.permissions';
import { LineItemsPermissions } from '@payaca/permissions/lineItems/line-items.permissions';
import { MaterialsPermissions } from '@payaca/permissions/materials/materials.permissions';
import { ScheduledEventsPermissions } from '@payaca/permissions/scheduledEvents/scheduled-events.permissions';
import { ServicePlanPermissions } from '@payaca/permissions/service-plans/service-plans.permissions';
import { StockLocationsPermissions } from '@payaca/permissions/stockLocations/stockLocations.permissions';
import { SuppliersPermissions } from '@payaca/permissions/suppliers/suppliers.permissions';
import { TasksPermissions } from '@payaca/permissions/tasks/tasks.permissions';
import { TimelogsPermissions } from '@payaca/permissions/timelogs/timelogs.permissions';
import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { AnimatePresence, easeOut, motion } from 'motion/react';
import { FC, Fragment, PropsWithChildren, useMemo } from 'react';
import { matchPath, NavLink, NavLinkProps } from 'react-router-dom';

const NavItem: FC<
  PropsWithChildren<
    {
      iconName: TIconName;
      disabled?: boolean;
    } & Omit<NavLinkProps, 'activeClassName'>
  >
> = (props) => {
  const { iconName, disabled, children, ...rest } = props;

  return (
    <MenuItemCore
      as={NavLink}
      iconName={iconName}
      disabled={disabled}
      wrapperClassName={clstx(
        'no-underline text-blue-900 font-normal',
        'hover:not-data-disabled:bg-gray-200 hover:not-disabled:bg-gray-200'
      )}
      activeClassName="!bg-gray-200"
      {...rest}
    >
      {children}
    </MenuItemCore>
  );
};

const NavigationSidebar: FC = () => {
  const { userHasRequiredPermission } = usePermissions();
  // When the user can view Stock Locations, Materials, Suppliers and Stock
  // locations are grouped under a collapsible "Stock" heading. Otherwise the
  // navigation is unchanged: Materials and Suppliers stay as top-level items.
  const hasStockLocations = userHasRequiredPermission([
    StockLocationsPermissions.GET_STOCK_LOCATIONS,
  ]);
  const { showParentAccountAdminLayout } = useShowParentAccountAdminLayout();
  const { analytics } = useGetMe();
  const hasServicePlansFeature = useMyAccountHasFeature('servicePlans');

  const hasInboundEmailsPermission = userHasRequiredPermission([
    InboundEmailsPermissions.GET_INBOUND_EMAILS,
  ]);
  const { data: inboundEmailUnreadCountData } = useGetInboundEmailUnreadCount({
    enabled: hasInboundEmailsPermission,
  });
  const inboundEmailUnreadCount =
    inboundEmailUnreadCountData?.me?.inboundEmailUnreadCount ?? 0;

  const accountAccessInformation = useSelector(
    (state) => state.account.accountAccessInformation
  );

  const trialPeriodIsExpired = useMemo(() => {
    return accountAccessInformation
      ? !accountAccessInformation.hasActiveSubscription &&
          accountAccessInformation.isTrialPeriodExpired
      : false;
  }, [accountAccessInformation]);

  const hasExceededUserSeats = useMemo(() => {
    if (!accountAccessInformation) return false;
    return (
      accountAccessInformation.hasActiveSubscription &&
      accountAccessInformation.hasExceededActiveSubscriptionUserSeats
    );
  }, [accountAccessInformation]);

  const isDisabled = trialPeriodIsExpired || hasExceededUserSeats;

  if (showParentAccountAdminLayout) {
    return (
      <nav className="space-y-1">
        <PermissionGuard
          renderIfHasPermissions={[AccountsPermissions.VIEW_CHILD_ACCOUNTS]}
        >
          <NavItem iconName="building-01.3" to="/accounts">
            Accounts
          </NavItem>
        </PermissionGuard>
      </nav>
    );
  }

  return (
    <nav className="space-y-1 overflow-y-auto">
      {/* Dashboard */}
      <PermissionGuard
        renderIfHasPermissions={[
          DashboardPermissions.GET_ACTION_REQUIRED_DRAFT_JOBS,
        ]}
      >
        <NavItem
          iconName="home-02.3"
          to="/dashboard"
          disabled={isDisabled}
          isActive={(match, location) => {
            return !!match || location.pathname === '/dashboard-new';
          }}
        >
          Dashboard
        </NavItem>
      </PermissionGuard>

      {/* Analytics */}
      <PermissionGuard
        renderIfHasPermissions={[AnalyticsPermissions.VIEW_EMBEDDED_DASHBOARD]}
      >
        {analytics && (
          <AnalyticsDisclosure analytics={analytics} disabled={isDisabled} />
        )}
      </PermissionGuard>

      {/* Projects */}
      <PermissionGuard
        renderIfHasPermissions={[
          DealsPermissions.GET_LISTED_DEALS,
          DealsPermissions.GET_MY_LISTED_DEALS,
        ]}
      >
        <NavItem
          iconName="lightbulb-05.3"
          to="/deals"
          exact={false}
          disabled={isDisabled}
          isActive={(match, location) => {
            return (
              !!match ||
              !!matchPath(location.pathname, {
                path: '/pipelines',
                exact: false,
              }) ||
              !!matchPath(location.pathname, {
                path: '/projects',
                exact: false,
              })
            );
          }}
        >
          Projects
        </NavItem>
      </PermissionGuard>

      {/* Invoices */}
      <PermissionGuard
        renderIfHasPermissions={[InvoicesPermissions.GET_LISTED_INVOICES]}
      >
        <NavItem iconName="file-06.3" to="/invoices" disabled={isDisabled}>
          Invoices
        </NavItem>
      </PermissionGuard>

      {/* Service Plans */}
      {hasServicePlansFeature && (
        <PermissionGuard
          renderIfHasPermissions={[ServicePlanPermissions.GET_SERVICE_PLANS]}
        >
          <NavItem
            iconName="repeat-04.3"
            to="/service-plans"
            disabled={isDisabled}
          >
            Service plans
          </NavItem>
        </PermissionGuard>
      )}

      {/* Customers */}
      <PermissionGuard
        renderIfHasPermissions={[
          CustomersPermissions.GET_COLLECTION_MANAGEMENT_VIEW,
        ]}
      >
        <NavItem iconName="users-01.3" to="/customers" disabled={isDisabled}>
          Customers
        </NavItem>
      </PermissionGuard>

      {/* Schedule */}
      <PermissionGuard
        renderIfHasPermissions={[
          ScheduledEventsPermissions.GET_MY_EVENTS,
          ScheduledEventsPermissions.GET_EVENTS,
        ]}
      >
        <NavItem
          iconName="calendar-check-02.3"
          to="/schedule"
          disabled={isDisabled}
        >
          Schedule
        </NavItem>
      </PermissionGuard>

      {/* Tasks */}
      <PermissionGuard
        renderIfHasPermissions={[
          TasksPermissions.GET_TASKS,
          TasksPermissions.GET_SELF_ASSIGNED_TASKS,
        ]}
      >
        <NavItem iconName="list.3" to="/tasks" disabled={isDisabled}>
          Tasks
        </NavItem>
      </PermissionGuard>

      {/* Timelogs */}
      <PermissionGuard
        renderIfHasPermissions={[TimelogsPermissions.PREVIEW_TIMELOGS]}
      >
        <NavItem
          iconName="clock-stopwatch.3"
          to="/timelogs"
          disabled={isDisabled}
        >
          Timelogs
        </NavItem>
      </PermissionGuard>

      {/* Items */}
      <PermissionGuard
        renderIfHasPermissions={[
          LineItemsPermissions.GET_COLLECTION_MANAGEMENT_VIEW,
        ]}
      >
        <NavItem
          iconName="shopping-cart-02.3"
          to="/items"
          disabled={isDisabled}
        >
          Items
        </NavItem>
      </PermissionGuard>

      {hasStockLocations ? (
        <StockDisclosure disabled={isDisabled} />
      ) : (
        <>
          <MaterialsNavItem disabled={isDisabled} />
          <SuppliersNavItem disabled={isDisabled} />
        </>
      )}

      {/* Automations */}
      <PermissionGuard
        renderIfHasPermissions={[AutomationsPermissions.GET_LISTED_AUTOMATIONS]}
      >
        <NavItem
          iconName="dataflow-03.3"
          to="/automations"
          disabled={trialPeriodIsExpired}
        >
          Automations
        </NavItem>
      </PermissionGuard>

      {/* Inbox */}
      <PermissionGuard
        renderIfHasPermissions={[InboundEmailsPermissions.GET_INBOUND_EMAILS]}
      >
        <NavItem iconName="inbox-01.3" to="/inbox" disabled={isDisabled}>
          <span className="flex flex-1 items-center">
            <Text as="span">Inbox</Text>
            {inboundEmailUnreadCount > 0 && (
              <Badge
                className="ml-auto"
                variant="solid"
                colour="blue"
                size="sm"
              >
                {new Intl.NumberFormat(window.navigator.language, {
                  notation: 'compact',
                }).format(inboundEmailUnreadCount)}
              </Badge>
            )}
          </span>
        </NavItem>
      </PermissionGuard>
    </nav>
  );
};

export default NavigationSidebar;

const MaterialsNavItem: FC<{ disabled?: boolean }> = (props) => {
  const { disabled } = props;

  return (
    <PermissionGuard
      renderIfHasPermissions={[
        MaterialsPermissions.GET_COLLECTION_MANAGEMENT_VIEW,
      ]}
    >
      <NavItem iconName="file-check-02.3" to="/materials" disabled={disabled}>
        Materials
      </NavItem>
    </PermissionGuard>
  );
};

const SuppliersNavItem: FC<{ disabled?: boolean }> = (props) => {
  const { disabled } = props;

  return (
    <PermissionGuard
      renderIfHasPermissions={[
        SuppliersPermissions.GET_COLLECTION_MANAGEMENT_VIEW,
      ]}
    >
      <NavItem iconName="truck-01.3" to="/suppliers" disabled={disabled}>
        Suppliers
      </NavItem>
    </PermissionGuard>
  );
};

const CollapsibleNavSection: FC<
  PropsWithChildren<{
    iconName: TIconName;
    label: string;
    defaultOpen: boolean;
  }>
> = (props) => {
  const { iconName, label, defaultOpen, children } = props;

  return (
    <Disclosure defaultOpen={defaultOpen}>
      {({ open }) => (
        <>
          <MenuItemCore
            as={DisclosureButton}
            iconName={iconName}
            wrapperClassName={clstx(
              'no-underline text-blue-900 font-normal',
              'hover:not-data-disabled:bg-gray-200 hover:not-disabled:bg-gray-200'
            )}
          >
            <span className="flex-1 text-left">
              <Text as="span">{label}</Text>
            </span>
            <UntitledIcon
              name="chevron-down"
              className={clstx(
                'size-4 transition-transform duration-300',
                open && 'rotate-180'
              )}
            />
          </MenuItemCore>
          <div className="overflow-hidden">
            <AnimatePresence initial={false}>
              {open && (
                <DisclosurePanel static as={Fragment}>
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: easeOut }}
                    className="overflow-hidden"
                  >
                    <div className="ml-5 space-y-1 border-l-2 border-gray-100 pl-3">
                      {children}
                    </div>
                  </motion.div>
                </DisclosurePanel>
              )}
            </AnimatePresence>
          </div>
        </>
      )}
    </Disclosure>
  );
};

const StockDisclosure: FC<{ disabled: boolean }> = (props) => {
  const { disabled } = props;

  return (
    <CollapsibleNavSection
      iconName="package.3"
      label="Stock"
      defaultOpen={['/materials', '/suppliers', '/stock-locations'].some(
        (path) => matchPath(location.pathname, path) !== null
      )}
    >
      <MaterialsNavItem disabled={disabled} />
      <SuppliersNavItem disabled={disabled} />
      <NavItem
        iconName="building-01.3"
        to="/stock-locations"
        disabled={disabled}
      >
        Stock locations
      </NavItem>
    </CollapsibleNavSection>
  );
};

type AnalyticsDisclosureProps = {
  analytics: NonNullable<ReturnType<typeof useGetMe>['analytics']>;
  disabled: boolean;
};

const AnalyticsDisclosure: FC<AnalyticsDisclosureProps> = (props) => {
  const { analytics, disabled } = props;

  return (
    <CollapsibleNavSection
      iconName="line-chart-up-03.3"
      label="Analytics"
      defaultOpen={matchPath(location.pathname, '/analytics') !== null}
    >
      {analytics.embeddableDashboards.map((item) => (
        <PermissionGuard
          key={item.uniqueIdentifier}
          renderIfHasPermissions={[
            AnalyticsPermissions.VIEW_EMBEDDED_DASHBOARD,
          ]}
        >
          <NavItem
            iconName="line-chart-up-03.3"
            to={`/analytics/embed/${item.uniqueIdentifier}`}
            disabled={disabled}
          >
            {item.title}
          </NavItem>
        </PermissionGuard>
      ))}
    </CollapsibleNavSection>
  );
};
